// ============================================================================
// Phase3RoleTechSelector.js
// Modern role type and tech stack selector for Phase 3 interview scheduling
// Features: AI-recommended roles from requirement, search, category filter, chip-based tech
// Used by: CompanyAdminDashboard, UserDashboard, MasterAdminDashboard
// ============================================================================

import React, { useState, useMemo, useEffect } from 'react';
import { ROLE_TECH_MAP, ROLE_CATEGORIES, getRecommendedRoles } from './Phase3RoleTechConfig';

const Phase3RoleTechSelector = ({
  roleType,
  setRoleType,
  selectedTechStacks,
  setSelectedTechStacks,
  selectedRequirementTitle,   // job title of selected requirement (for AI matching)
  suggestedTechs,             // JD/intake must-have technologies — pre-filled once per requirement
  suggestionKey,              // the requirement the suggestions belong to
}) => {
  const [roleSearch, setRoleSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [showAllRoles, setShowAllRoles] = useState(false);
  const [customTechInput, setCustomTechInput] = useState('');
  const [customTechs, setCustomTechs] = useState(new Set()); // tracks which are user-added
  const [prefilledKey, setPrefilledKey] = useState(null);    // requirement whose must-haves were pre-filled

  // ── AI-recommended roles based on selected requirement ──
  const { recommended, topMatch } = useMemo(() => {
    return getRecommendedRoles(selectedRequirementTitle);
  }, [selectedRequirementTitle]);

  // Auto-select top match when requirement changes
  useEffect(() => {
    if (topMatch && !roleType) {
      setRoleType(topMatch);
      setSelectedTechStacks([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topMatch]);

  // Pre-fill the tech stack from the JD/intake must-haves — once per requirement, only while
  // nothing is selected (never overwrites the recruiter's choices; everything stays editable).
  useEffect(() => {
    if (!roleType || !suggestionKey || prefilledKey === suggestionKey) return;
    if (!suggestedTechs || suggestedTechs.length === 0 || selectedTechStacks.length > 0) return;
    const roleChips = ROLE_TECH_MAP[roleType] || [];
    const picked = [];
    const custom = [];
    suggestedTechs.forEach((tech) => {
      const chip = roleChips.find((c) => c.toLowerCase() === String(tech).toLowerCase());
      if (chip) { if (!picked.includes(chip)) picked.push(chip); }
      else if (!custom.includes(tech)) custom.push(tech);
    });
    setSelectedTechStacks([...picked, ...custom]);
    setCustomTechs(new Set(custom));               // not in this role's chip list → shown as custom chips
    setPrefilledKey(suggestionKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleType, suggestionKey, suggestedTechs]);

  // ── Filter roles by search + category ──
  const allRoles = Object.keys(ROLE_TECH_MAP);

  const filteredRoles = useMemo(() => {
    let roles = allRoles;

    // Filter by category
    if (activeCategory !== 'all') {
      roles = ROLE_CATEGORIES[activeCategory] || [];
    }

    // Filter by search
    if (roleSearch.trim()) {
      const q = roleSearch.toLowerCase();
      roles = roles.filter(r => r.toLowerCase().includes(q));
    }

    return roles;
  }, [roleSearch, activeCategory, allRoles]);

  // ── Get category for a role ──
  const getCategoryForRole = (role) => {
    for (const [cat, roles] of Object.entries(ROLE_CATEGORIES)) {
      if (roles.includes(role)) return cat;
    }
    return '';
  };

  // ── Category icons ──
  const categoryIcons = {
    'all': '🔍',
    'Software Engineering': '💻',
    'Data & AI': '🧠',
    'Infrastructure & Operations': '☁️',
    'Quality & Security': '🛡️',
    'Specialized Engineering': '⚙️',
    'Architecture & Leadership': '🏗️',
    'Product & Design Engineering': '🎨',
  };

  const handleRoleSelect = (role) => {
    setRoleType(role);
    setSelectedTechStacks([]);
    setCustomTechs(new Set());
    setCustomTechInput('');
    setRoleSearch('');
  };

  const handleTechToggle = (tech) => {
    if (selectedTechStacks.includes(tech)) {
      setSelectedTechStacks(prev => prev.filter(t => t !== tech));
      // Also remove from custom set if it was custom
      setCustomTechs(prev => {
        const next = new Set(prev);
        next.delete(tech);
        return next;
      });
    } else {
      setSelectedTechStacks(prev => [...prev, tech]);
    }
  };

  const handleAddCustomTech = () => {
    const trimmed = customTechInput.trim();
    if (!trimmed) return;
    // Prevent duplicates
    if (selectedTechStacks.includes(trimmed)) {
      setCustomTechInput('');
      return;
    }
    setSelectedTechStacks(prev => [...prev, trimmed]);
    setCustomTechs(prev => new Set(prev).add(trimmed));
    setCustomTechInput('');
  };

  const handleCustomTechKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCustomTech();
    }
  };

  return (
    <div className="space-y-5">

      {/* ════════════════════════════════════════════════════════ */}
      {/* ROLE TYPE SELECTOR                                      */}
      {/* ════════════════════════════════════════════════════════ */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Role Type *
          {selectedRequirementTitle && recommended.length > 0 && (
            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              ✨ AI Matched
            </span>
          )}
        </label>

        {/* ── Selected Role Display ── */}
        {roleType && (
          <div className="mb-3 flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl">
            <div className="flex-1">
              <span className="text-xs font-medium text-blue-500 uppercase tracking-wide">
                {getCategoryForRole(roleType)}
              </span>
              <p className="font-bold text-gray-900 text-base">{roleType}</p>
            </div>
            {recommended.includes(roleType) && (
              <span className="px-2.5 py-1 bg-blue-600 text-white text-xs font-bold rounded-lg">
                Recommended
              </span>
            )}
            <button
              onClick={() => { setRoleType(''); setSelectedTechStacks([]); }}
              className="text-gray-400 hover:text-red-500 transition-colors p-1"
              title="Change role"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* ── Role Selector Panel (shown when no role selected) ── */}
        {!roleType && (
          <div className="border-2 border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm">

            {/* Search Bar */}
            <div className="p-3 border-b border-gray-100 bg-gray-50">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={roleSearch}
                  onChange={(e) => setRoleSearch(e.target.value)}
                  placeholder="Search roles... (e.g., Backend, ML Engineer, DevOps)"
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 bg-white transition-all"
                />
              </div>
            </div>

            {/* Category Pills */}
            <div className="px-3 py-2 border-b border-gray-100 overflow-x-auto">
              <div className="flex gap-1.5 min-w-max">
                <button
                  onClick={() => setActiveCategory('all')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                    activeCategory === 'all'
                      ? 'bg-gray-800 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {categoryIcons['all']} All Roles
                </button>
                {Object.keys(ROLE_CATEGORIES).map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                      activeCategory === cat
                        ? 'bg-gray-800 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {categoryIcons[cat]} {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* AI Recommended Section */}
            {recommended.length > 0 && activeCategory === 'all' && !roleSearch && (
              <div className="px-3 pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">✨ Recommended for "{selectedRequirementTitle}"</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-2">
                  {recommended.map((role, idx) => (
                    <button
                      key={role}
                      onClick={() => handleRoleSelect(role)}
                      className="group flex items-center gap-3 p-3 rounded-xl border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 transition-all text-left"
                    >
                      <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center text-xs font-bold shrink-0">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{role}</p>
                        <p className="text-xs text-blue-600">{getCategoryForRole(role)}</p>
                      </div>
                      <svg className="w-4 h-4 text-blue-400 group-hover:text-blue-600 shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  ))}
                </div>
                <div className="border-t border-gray-200 mb-1"></div>
                <button
                  onClick={() => setShowAllRoles(!showAllRoles)}
                  className="w-full py-2 text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {showAllRoles ? '▲ Hide other roles' : '▼ Show all roles'}
                </button>
              </div>
            )}

            {/* Full Role List */}
            {(showAllRoles || !recommended.length || roleSearch || activeCategory !== 'all') && (
              <div className="max-h-64 overflow-y-auto px-3 py-2">
                {filteredRoles.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">No roles match your search</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {filteredRoles
                      .filter(role => !recommended.includes(role) || roleSearch || activeCategory !== 'all')
                      .map(role => (
                      <button
                        key={role}
                        onClick={() => handleRoleSelect(role)}
                        className="group flex items-center gap-3 p-2.5 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all text-left"
                      >
                        <div className="w-7 h-7 rounded-lg bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center text-xs shrink-0 transition-colors">
                          {categoryIcons[getCategoryForRole(role)] || '📋'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{role}</p>
                          <p className="text-xs text-gray-400">{getCategoryForRole(role)}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════════════════════ */}
      {/* TECH STACK SELECTOR (chip/pill style)                   */}
      {/* ════════════════════════════════════════════════════════ */}
      {roleType && ROLE_TECH_MAP[roleType] && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-semibold text-gray-700">
              Tech Stack *
              <span className="text-gray-400 font-normal ml-1">
                ({selectedTechStacks.length} of {ROLE_TECH_MAP[roleType].length} selected)
              </span>
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedTechStacks([...ROLE_TECH_MAP[roleType], ...Array.from(customTechs)])}
                className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
              >
                Select All
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => { setSelectedTechStacks([]); setCustomTechs(new Set()); }}
                className="text-xs font-semibold text-gray-500 hover:text-red-600 transition-colors"
              >
                Clear
              </button>
            </div>
          </div>

          {prefilledKey && prefilledKey === suggestionKey && (
            <p className="text-xs text-blue-700 mb-2" data-testid="techstack-prefilled">
              ✨ Pre-filled from the JD/intake must-haves — add or remove as needed.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {ROLE_TECH_MAP[roleType].map(tech => {
              const isSelected = selectedTechStacks.includes(tech);
              return (
                <button
                  key={tech}
                  onClick={() => handleTechToggle(tech)}
                  className={`
                    inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium
                    border-2 transition-all cursor-pointer select-none
                    ${isSelected
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm hover:bg-blue-700'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                    }
                  `}
                >
                  {isSelected && (
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                  {tech}
                </button>
              );
            })}
          </div>

          {/* Custom tech chips */}
          {customTechs.size > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {Array.from(customTechs).filter(t => selectedTechStacks.includes(t)).map(tech => (
                <button
                  key={tech}
                  onClick={() => handleTechToggle(tech)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium
                    border-2 transition-all cursor-pointer select-none
                    bg-amber-500 text-white border-amber-500 shadow-sm hover:bg-amber-600"
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {tech}
                  <span className="ml-0.5 text-xs bg-amber-600 text-amber-100 px-1.5 py-0.5 rounded-full">Custom</span>
                </button>
              ))}
            </div>
          )}

          {/* Add custom tech input */}
          <div className="mt-3 flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                value={customTechInput}
                onChange={(e) => setCustomTechInput(e.target.value)}
                onKeyDown={handleCustomTechKeyDown}
                placeholder="Add custom tech (e.g., Snowflake, dbt, Pulumi)..."
                className="w-full px-4 py-2.5 text-sm border-2 border-dashed border-gray-300 rounded-xl
                  focus:ring-2 focus:ring-amber-400 focus:border-amber-400 bg-white transition-all
                  placeholder:text-gray-400"
                maxLength={50}
              />
            </div>
            <button
              onClick={handleAddCustomTech}
              disabled={!customTechInput.trim()}
              className="px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold rounded-xl
                transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap
                shadow-sm hover:shadow-md"
            >
              + Add
            </button>
          </div>

          {/* Selection summary */}
          {selectedTechStacks.length > 0 && (
            <div className="mt-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Selected Stack</p>
              <p className="text-sm text-gray-800 font-medium">
                {selectedTechStacks.map((tech, idx) => (
                  <span key={tech}>
                    {idx > 0 && ' · '}
                    {customTechs.has(tech) ? (
                      <span className="text-amber-700 font-bold">{tech}*</span>
                    ) : (
                      tech
                    )}
                  </span>
                ))}
              </p>
              {customTechs.size > 0 && (
                <p className="text-xs text-amber-600 mt-1">* Custom technology</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Phase3RoleTechSelector;